import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { notification } from 'antd';

import { AUTH_TEXT } from '../constants/text';

/** The `?connect=` outcome the API appends to `returnTo` when it hands the browser back. */
export const CONNECT_PARAM = 'connect';

/**
 * Surfaces the result of a Sorare OAuth round-trip, then removes the marker from the URL.
 *
 * This deliberately lives at the layout level rather than inside `ConnectSorareButton`. On
 * mobile that button renders inside the nav `Drawer`'s `extra`, and antd does not mount a
 * closed drawer's contents — so an effect hosted there would not run until the user happened
 * to tap the hamburger. A failed connect would show no error at all and leave `?connect=error`
 * stuck in the address bar. `AppLayout` is always mounted, on both breakpoints.
 *
 * Only the `connect` key is stripped: a blanket `navigate(pathname)` would discard whatever
 * filters the user had applied before they started connecting.
 */
export const useConnectOutcome = (): React.ReactElement | null => {
  const location = useLocation();
  const navigate = useNavigate();
  const [api, contextHolder] = notification.useNotification();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const outcome = params.get(CONNECT_PARAM);
    if (!outcome) {
      return;
    }

    if (outcome === 'denied') {
      api.warning({
        title: AUTH_TEXT.CONNECT_DENIED_TITLE,
        description: AUTH_TEXT.CONNECT_DENIED,
        placement: 'bottomRight',
      });
    } else if (outcome === 'error') {
      api.error({
        title: AUTH_TEXT.CONNECT_FAILED_TITLE,
        description: AUTH_TEXT.CONNECT_FAILED,
        placement: 'bottomRight',
      });
    }

    params.delete(CONNECT_PARAM);
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  }, [api, location.pathname, location.search, navigate]);

  return contextHolder;
};
